<template>
  <div>
    <DxDataGrid
      ref="dataGrid"
      :data-source="sampleDetails"
      :show-borders="false"
      :show-column-lines="false"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :column-auto-width="true"
      :show-column-headers="true"
      :show-colon="false"
      :remote-operations="false"
      :hover-state-enabled="false"
      :show-row-lines="false"
      :row-alternation-enabled="false"
      class="custom-grid custom-grid-detail-agronomy"
      :no-data-text="$t('noDataTextDetail')"
      @exporting="onExporting"
      @toolbar-preparing="onToolbarPreparing($event)"
      @contentReady="onInit"
    >
      <DxExport :enabled="true" />
      <DxColumn
        :group-index="0"
        caption="Grupo"
        data-field="parameter_group"
      />
      <DxColumn
        data-field="parameter"
        :caption="$t('foodSamples.parameter')"
        alignment="left"
      />
      <DxColumn
        name="colValueResultado"
        :caption="$t('foodSamples.result')"
        alignment="center"
        :calculate-cell-value="calcResultadoText"
        :customize-text="identityCustomizeText"
      />

      <DxColumn
        alignment="center"
        name="colValueRango"
        :caption="$t('agronomySample.summary.avgRange')"
        :calculate-cell-value="calcRangoText"
        :customize-text="identityCustomizeText"
      />

      <DxColumn
        name="colValueInterpretacion"
        :allow-grouping="false"
        :allow-exporting="false"
        :caption="$t('agronomySample.summary.interpretation')"
        alignment="center"
        :calculate-cell-value="calcInterpretacionText"
        cell-template="interpretationCellTemplate"
        css-class="bullet"
      />

      <DxSearchPanel
        :visible="true"
        :search-visible-columns-only="true"
        :placeholder="$t('search')"
        :width="239"
      />

      <!-- <DxGrouping expand-mode="rowClick" :auto-expand-all="true" /> -->
      <DxPager
        :allowed-page-sizes="[10, 25, 50, 100]"
        :show-page-size-selector="true"
        info-text="{2} items"
        :visible="false"
      />
      <DxPaging :page-size="100" />
      <template #interpretationCellTemplate="{ data: cellData }">
        <InterpretationCell
          :cell-data="cellData"
          :max-value="getMaxValueParameter"
        />
      </template>
    </DxDataGrid>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import {
  DxDataGrid,
  DxColumn,
  // DxGrouping,
  // DxGroupPanel,
  DxPager,
  DxPaging,
  DxSearchPanel,
  DxExport,
} from 'devextreme-vue/data-grid';
import saveAs from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';

import InterpretationCell from '../InterpretationCell/InterpretationCell.vue';

export default {
  components: {
    DxDataGrid,
    DxColumn,
    // DxGrouping,
    // DxGroupPanel,
    DxPager,
    DxPaging,
    DxSearchPanel,
    InterpretationCell,
    DxExport,
  },
  computed: {
    ...mapState('filter', ['currentSampleId']),
    ...mapState('agronomySamples', ['sampleDetails']),
    ...mapState('samples', ['reportSampleCode']),
    getMaxValueParameter() {
      const values = [];

      this.sampleDetails.forEach((sample) => {
        values.push(sample.value.result);
      });
      return Math.max(...values);
    },
  },
  methods: {
    numericDisplayResult(value) {
      const s =
        value?.ResultadoPresentacion ?? value?.result_round ?? null;
      if (typeof s === 'string' && s.length > 0) {
        const n = Number(s.replace(/\./g, '').replace(',', '.'));
        if (!Number.isNaN(n)) return n;
      }
      return Number(value?.result);
    },
    getRangeLevels(value) {
      // Algunos análisis traen `unit_new_id` pero no informan los límites convertidos.
      const hasNewLevels =
        Number(value?.unit_new_id) > 0 &&
        value?.low_level_new != null &&
        value?.high_level_new != null;

      return {
        low: hasNewLevels ? value.low_level_new : value?.low_level,
        high: hasNewLevels ? value.high_level_new : value?.high_level,
      };
    },
    getInterpretationTextFromValue(value) {
      if (!value) return '';

      const n = this.numericDisplayResult(value);
      const veryLow = value?.very_low_level;
      const low = value?.low_level;
      const high = value?.high_level;
      const veryHigh = value?.very_high_level;

      if (
        [n, veryLow, low, high, veryHigh].some(
          (level) => typeof level !== 'number' || Number.isNaN(level)
        )
      ) {
        return '';
      }

      // La escala visual y el texto deben clasificar igual para no desalinear búsqueda y UI.
      if (n >= veryHigh) return this.$t('veryHighLevel');
      if (n >= high) return this.$t('highLevel');
      if (n >= low) return this.$t('optimalLevel');
      if (n >= veryLow) return this.$t('lowLevel');

      return this.$t('veryLowLevel');
    },
    // Devuelve el string EXACTO que quieres que se vea y que sea buscable
    calcResultadoText(row) {
      const v = row?.value;
      if (!v) return '';

      const pres = v.ResultadoPresentacion || '';
      const isInProcess = pres.includes('En Proceso');

      const texto = isInProcess ? this.$t('inProcess') : pres;
      const unidad = isInProcess ? '' : v.unit || '';

      return `${texto} ${unidad}`.trim();
    },

    calcRangoText(row) {
      const v = row?.value;
      if (!v) return '';

      const { low, high } = this.getRangeLevels(v);
      if (low == null && high == null) return '';
      return `${low} - ${high}`.trim();
    },

    // Devuelve el texto de interpretación para que el SearchPanel pueda filtrar
    calcInterpretacionText(row) {
      return this.getInterpretationTextFromValue(row?.value);
    },

    // customizeText “neutro”: muestra tal cual lo calculado por calculateCellValue
    identityCustomizeText(e) {
      return e.value ?? '';
    },

    interpretationValueText(e) {
      return this.getInterpretationTextFromValue(e?.value);
    },
    onInit() {
      Array.from(
        document.getElementsByClassName('dx-group-cell')
      ).forEach((el) => {
        el.innerHTML = el.innerHTML.split(':')[1].trim();
      });
    },
    rangeValueText(e) {
      const { low, high } = this.getRangeLevels(e?.value);
      if (low == null && high == null) return '';
      return `${low || ''} - ${high || ''}`;
    },
    analyticValueText(e) {
      return `${
        e.value.ResultadoPresentacion.includes('En Proceso')
          ? this.$t('inProcess')
          : e.value.ResultadoPresentacion
      }
      ${
        e.value.ResultadoPresentacion.includes('En Proceso')
          ? ''
          : e.value.unit
      }`;
    },
    onToolbarPreparing(e) {
      const toolbarItems = e.toolbarOptions.items;

      e.toolbarOptions.elementAttr = {
        class: 'custom-toolbar',
      };

      toolbarItems.forEach((item) => {
        if (item.name === 'searchPanel') {
          item.location = 'after';
        }
        if (item.name === 'exportButton') {
          item.location = 'after';
        }

        // if (item.name === 'groupPanel') {
        //   item.location = 'after';
        // }
      });

      // Añadir el selector de agrupaciones
      // toolbarItems.push({
      //   name: 'Filtros',
      //   location: 'after',
      //   widget: 'dxButton',
      //   locateInMenu: 'never',
      //   options: {
      //     text: '',
      //     icon: '././static/icons/ic_filter.svg',
      //     width: 36,
      //     elementAttr: {
      //       class: 'btn-filtrar',
      //     },
      //     onClick: () => {
      //       this.$f7.panel.open('right');
      //     },
      //   },
      // });
    },
    onExporting(e) {
      e.component.beginUpdate();
      e.component.columnOption('ID', 'visible', true);
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet(
        `${`${this.$t(
          'agronomySample.summary.sampleSummary'
        )}-${this.$t('Agronomy')}`}`
      );

      exportDataGrid({
        component: e.component,
        worksheet,
      })
        .then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(
              new Blob([buffer], {
                type: 'application/octet-stream',
              }),
              `besafer-${this.reportSampleCode}.xlsx`
            );
          });
        })
        .then(() => {
          e.component.columnOption('ID', 'visible', false);
          e.component.endUpdate();
        });

      e.cancel = true;
    },
  },
};
</script>

<style>
.button.whiteFontColor {
  display: inline-flex !important;
  align-items: center;
  gap: 8px;
}

.button .icon-wrap {
  display: inline-flex;
  align-items: center;
  line-height: 0;
}

.button .icon-wrap svg {
  display: block;
  width: 20px;
  height: 20px;
}

.custom-grid-detail-agronomy .dx-searchbox .dx-texteditor-input {
  font-size: 14px !important;
  color: #333 !important;
  padding-left: 28px !important;
}

.custom-custom-grid-detail-agronomy
  .dx-datagrid-search-panel
  .dx-searchbox
  .dx-icon.dx-icon-search {
  color: #00346a !important;
  left: 8px !important;
}

.custom-grid-detail-agronomy .dx-toolbar-after {
  left: initial !important;
}

.custom-grid-detail-agronomy .dx-datagrid-headers {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
.custom-grid-detail-agronomy
  .dx-datagrid-headers
  .dx-datagrid-content {
  padding: 0 !important;
}
.custom-grid-detail-agronomy .dx-datagrid-headers .dx-datagrid-table {
  border-collapse: separate;
  border-spacing: 0 !important;
  width: 100%;
}

.custom-grid-detail-agronomy
  .dx-datagrid-headers
  .dx-header-row
  > td {
  border-color: #e6e9ef !important;
}

.custom-grid-detail-agronomy
  .dx-datagrid-rowsview
  .dx-row.dx-data-row
  > td:first-child,
.custom-grid-detail-agronomy
  .dx-datagrid-rowsview
  .dx-group-row
  > td:first-child {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}
.custom-grid-detail-agronomy
  .dx-datagrid-rowsview
  .dx-row.dx-data-row
  > td:last-child,
.custom-grid-detail-agronomy
  .dx-datagrid-rowsview
  .dx-group-row
  > td:last-child {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}

/* Fila de grupo en verde */
.custom-grid-detail-agronomy
  .dx-datagrid-rowsview
  .dx-group-row
  > td {
  background-color: #f2f8e2 !important;
}
.custom-grid-detail-agronomy .dx-datagrid-rowsview .dx-row {
  background: var(--Neutral-10, #ebf2fa);
}
.custom-grid-detail-agronomy .dx-searchbox {
  width: 200px !important;
}
.custom-grid-detail-agronomy .dx-toolbar-after {
  display: grid;
  grid-template-columns: 106px 100px 47px 36px;
  grid-template-rows: repeat(1, 1fr);
  row-gap: 24px;
  align-items: center;
  box-sizing: border-box;
}
.custom-grid-detail-agronomy .dx-toolbar-after :nth-child(1) {
  grid-row: 1;
  grid-column: 4;
}
.custom-grid-detail-agronomy .dx-toolbar-after :nth-child(2) {
  grid-row: 1;
  grid-column: 1 / span 2;
}
.custom-grid-detail-agronomy .dx-toolbar-after :nth-child(3) {
  grid-row: 1;
  grid-column: 3;
  justify-self: start;
}

/* === Quitar borde azul y usar el mismo verde en filas de grupo === */
.custom-grid-detail-agronomy
  .dx-datagrid-rowsview
  .dx-group-row
  > td {
  background-color: #f2f8e2 !important; /* mismo verde */
  border-color: #f2f8e2 !important; /* igualar el borde al fondo */
}

/* También eliminar cualquier sombra o línea superior/izquierda azul */
.custom-grid-detail-agronomy
  .dx-datagrid-rowsview
  .dx-group-row
  > td:not(:last-child) {
  border-right: 1px solid #f2f8e2 !important;
}

.custom-grid-detail-agronomy
  .dx-datagrid-rowsview
  .dx-group-row
  > td:first-child {
  border-left: 1px solid #f2f8e2 !important;
}

.custom-grid .dx-searchbox {
  border-radius: 24px !important;
  border: 1px solid var(--Primary-Blue-70, #00346a) !important;
  background: var(--Basic-White, #fff) !important;

  box-sizing: border-box;
}

/* === Botones junto al buscador (Filtros y Exportar) === */
.custom-grid-detail-agronomy .dx-toolbar-after .dx-button {
  height: 40px !important; /* igual altura que el search */
  min-width: 40px !important; /* ancho cuadrado */
  border-radius: 10px !important; /* esquinas suaves */
  border: 1px solid #00346a !important; /* mismo tono azul */
  display: flex !important;
  align-items: center;
  justify-content: center;
  background-color: #fff !important; /* fondo blanco como el buscador */
  box-sizing: border-box;
  padding: 0 !important;
}

/* Iconos dentro de los botones */
.custom-grid-detail-agronomy .dx-toolbar-after .dx-button .dx-icon {
  color: #00346a !important;
  font-size: 16px !important;
}

/* Hover opcional con tono más oscuro */
.custom-grid-detail-agronomy .dx-toolbar-after .dx-button:hover {
  background-color: #f5f9ff !important;
  border-color: #002c5c !important;
}

/* === Corregir el "salto" visual del buscador === */
.custom-grid-detail-agronomy .dx-datagrid-search-panel.dx-textbox {
  height: 40px !important;
  border: 1px solid #00346a !important;
  background-color: #fff !important;
  margin: 0 !important; /* elimina el margen naranja */
  padding: 0 !important;
  box-sizing: border-box;
}

/* Eliminar padding interno extra del contenedor */
.custom-grid-detail-agronomy
  .dx-datagrid-search-panel
  .dx-texteditor-container {
  margin: 0 !important;
  padding: 0 !important;
  top: 0 !important;
  bottom: 0 !important;
}

/* Alinear todo verticalmente en la toolbar */
.custom-grid-detail-agronomy .dx-toolbar-after {
  align-items: center !important;
}

/* Botones */
.custom-grid-detail-agronomy .dx-toolbar-after .dx-button {
  height: 40px !important;
  width: 40px !important;
  border: 1px solid #00346a !important;
  background-color: #fff !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  margin: 0 !important;
  padding: 0 !important;
}

/* === Centrar los iconos dentro de los botones perfectamente === */
.custom-grid-detail-agronomy
  .dx-toolbar-after
  .dx-button
  .dx-button-content {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  height: 100% !important;
  padding: 0 !important;
  margin: 0 !important;
}

/* Ajuste fino del icono para asegurar centrado perfecto */
.custom-grid-detail-agronomy .dx-toolbar-after .dx-button .dx-icon {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  height: 16px !important;
  width: 16px !important;
  position: relative;
  top: 0 !important; /* si siguen algo altos, cambia a top: 1px */
}

/* Input más grande */
.custom-grid-detail-agronomy .dx-searchbox .dx-texteditor-input {
  padding-left: 36px !important;
  height: 40px !important;
  line-height: 40px !important;
}

/* Contenedor */
.custom-grid-detail-agronomy .dx-searchbox {
  position: relative;
  height: 40px !important;
}

/* ICONO */
.custom-grid-detail-agronomy .dx-searchbox::before {
  content: '';
  position: absolute;

  left: 7px;
  top: 45%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;

  background-image: url('@/static/icons/ic_search_blue.svg');
  background-repeat: no-repeat;
  background-size: 24px 24px;

  pointer-events: none;
}
.custom-grid-detail-agronomy .dx-toolbar-after {
  display: grid;
  grid-template-columns: auto 40px;
  align-items: center;
  column-gap: 4px; /* separación buscador - excel */
}
</style>
